import { ArrowLeft, RefreshCcw } from "lucide-react"
import { useCallback, useRef, useState } from "react"
import { Button } from "shared-web/components/base/control/Button"
import { OtpInput } from "shared-web/components/composite/input/OtpInput"
import useKeyboardShortcut from "shared-web/hooks/useKeyboardShortcut"

import { ErrorMessage } from "./ErrorMessage"
import { useSignInContext } from "./SignInContext"
import {
  StepCard,
  StepCardBody,
  StepCardFooter,
  StepCardTitle,
} from "./StepCard"

interface StepOTPCodeProps {
  title: string
  sentTo: string
  onSubmit: (code: string) => void
  isSubmitting: boolean
  onClickSendAnotherCode: () => void
  isSendingAnotherCode: boolean
  errorMessage: string | null
}

const EMPTY_CODE_VALUES = ["", "", "", "", "", ""]

export function StepOTPCode(props: StepOTPCodeProps) {
  const {
    title,
    sentTo,
    onSubmit,
    isSubmitting,
    onClickSendAnotherCode,
    isSendingAnotherCode,
    errorMessage,
  } = props

  const [codeValues, setCodeValues] = useState(EMPTY_CODE_VALUES)

  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const onChangeCodeValues = useCallback(
    (codeValues: string[]) => {
      setCodeValues(codeValues)
      const code = codeValues.join("")
      if (code.length === codeValues.length) {
        onSubmit(code)
      }
    },
    [onSubmit]
  )

  const handleSubmit = useCallback(() => {
    const code = codeValues.join("")
    if (code.length !== codeValues.length) {
      return
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    onSubmit(code)
  }, [onSubmit, codeValues])

  const handleClickSendAnotherCode = useCallback(() => {
    setCodeValues(EMPTY_CODE_VALUES)
    onClickSendAnotherCode()
  }, [onClickSendAnotherCode])

  const { goBack } = useSignInContext()

  useKeyboardShortcut("Enter", handleSubmit)

  return (
    <StepCard>
      <StepCardTitle>{title}</StepCardTitle>
      <StepCardBody className="gap-y-4">
        <p className="text-body-lg color-subtle">
          A code was sent to {sentTo}.
        </p>
        <label className="text-body color-default font-medium">
          {isSubmitting ? "Verifying..." : "Enter code"}
        </label>
        <OtpInput
          values={codeValues}
          onChange={onChangeCodeValues}
          disabled={isSubmitting}
          invalid={!!errorMessage}
          inputTestId="code-input"
        />
        <ErrorMessage message={errorMessage} />
      </StepCardBody>
      <StepCardFooter className="flex flex-col gap-y-4">
        <Button
          mode="ghost"
          leftIcon={RefreshCcw}
          onClick={handleClickSendAnotherCode}
          loading={isSendingAnotherCode}
          className="w-full"
        >
          Send another code
        </Button>
        <Button
          mode="ghost"
          leftIcon={ArrowLeft}
          onClick={goBack}
          className="w-full"
        >
          Go back
        </Button>
      </StepCardFooter>
    </StepCard>
  )
}
